// ✅ src/pages/Vehicle/VehicleSubTypeListing.jsx
// ✅ UPDATED WITH LOCALSTORAGE HEADERS
//
// Backend mounted as:
//   app.use("/api/mainSubTypeListing", VehicleSubTypeListing);
//
// Endpoints:
// ✅ GET  /api/mainSubTypeListing/init?vehicleTypeId=1
// ✅ GET  /api/mainSubTypeListing/list?vehicleTypeId=1
// ✅ GET  /api/mainSubTypeListing/subtype-info?subTypeId=10
// ✅ POST /api/mainSubTypeListing/save-group    { vehicleTypeId, selectedSubTypeId }
// ✅ POST /api/mainSubTypeListing/remove        { removeIds: [..] }

import React, { useContext, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { FaSave, FaTrash, FaArrowLeft, FaFilter } from "react-icons/fa";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import HeaderBar from "../../components/HeaderBar";
import VehicleLayoutWrapper from "../../components/VehicleLayoutWrapper";
import { ThemeContext } from "../../context/ThemeContext";
import getCurrentSkin from "../../utils/getCurrentSkin";

const API = "http://nissi-india.com/api/mainSubTypeListing";

const pick = (row, keys, fallback = "") => {
  for (const k of keys) {
    const v = row?.[k];
    if (v !== undefined && v !== null && String(v).trim() !== "") return v;
  }
  return fallback;
};

export default function VehicleSubTypeListing() {
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;
  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;

  const navigate = useNavigate();

  // ✅ localStorage values
  const finYearId = localStorage.getItem("finYearId") || "";
  const finYear = localStorage.getItem("finYear") || "";
  const branchId = localStorage.getItem("branchId") || "";
  const branch = localStorage.getItem("branchType") || "";
  const userId = localStorage.getItem("userId") || "";

  // ✅ common headers
  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": branchId,
      "x-branch": branch,
      "x-userid": userId,
    }),
    [finYearId, finYear, branchId, branch, userId]
  );

  // ✅ ASPX uses Session["VehicleTypeID"] → here we take from query or sessionStorage
  const qs = useMemo(() => new URLSearchParams(window.location.search), []);
  const vehicleTypeId = useMemo(() => {
    const fromQS = qs.get("vehicletypeid") || qs.get("VehicleTypeID") || "";
    const fromSS =
      sessionStorage.getItem("vehicletypeid") ||
      sessionStorage.getItem("VehicleTypeID") ||
      "";
    const val = Number(fromQS || fromSS || 0);
    return Number.isFinite(val) ? val : 0;
  }, [qs]);

  // init data (ddl + index)
  const [mainTypeDropdown, setMainTypeDropdown] = useState([]);
  const [groupingDropdown, setGroupingDropdown] = useState([]);
  const [vehicleTypeIndex, setVehicleTypeIndex] = useState("");

  // current selections
  const [selectedMainType, setSelectedMainType] = useState("");
  const [selectedSubTypeToGroup, setSelectedSubTypeToGroup] = useState("Select");

  // subtype-info
  const [subTypeCount, setSubTypeCount] = useState("");
  const [subTypeTooltip, setSubTypeTooltip] = useState("");

  // list rows
  const [rows, setRows] = useState([]);

  // row selection for remove
  const [selectedIds, setSelectedIds] = useState([]);
  const allChecked = useMemo(
    () => rows.length > 0 && selectedIds.length === rows.length,
    [rows, selectedIds]
  );

  // UI
  const [showFilters, setShowFilters] = useState(true);
  const [loading, setLoading] = useState(false);

  // grid row mapping
  const getRowId = (r) =>
    Number(pick(r, ["fld_vehicletypeid", "VehicleTypeID", "vehicletypeid", "id"], 0)) || 0;

  const getRowName = (r) =>
    pick(r, ["fld_vehicletypename", "Vehicle Type", "VehicleType", "name"], "-");

  // ---------------- API Calls ----------------

  const fetchInit = async () => {
    if (!vehicleTypeId) return;
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/init`, {
        params: { vehicleTypeId },
        headers: commonHeaders,
      });

      if (data?.success === false) throw new Error(data?.message || "Init failed");

      const d = data?.data || {};
      setMainTypeDropdown(d.mainTypeDropdown || []);
      setGroupingDropdown(d.groupingDropdown || []);
      setVehicleTypeIndex(d.vehicleTypeIndex ?? "");
      setSelectedMainType(String(vehicleTypeId));
    } catch (e) {
      console.error(e);
      toast.error(e?.response?.data?.message || e?.message || "Init error");
    } finally {
      setLoading(false);
    }
  };

  const fetchList = async () => {
    if (!vehicleTypeId) return;
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/list`, {
        params: { vehicleTypeId },
        headers: commonHeaders,
      });

      if (data?.success === false) throw new Error(data?.message || "List failed");

      setRows(data?.data || []);
      setSelectedIds([]);
    } catch (e) {
      console.error(e);
      toast.error(e?.response?.data?.message || e?.message || "Load error");
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchSubTypeInfo = async (subTypeId) => {
    if (!subTypeId || subTypeId === "Select") {
      setSubTypeCount("");
      setSubTypeTooltip("");
      return;
    }

    try {
      const { data } = await axios.get(`${API}/subtype-info`, {
        params: { subTypeId },
        headers: commonHeaders,
      });

      if (data?.success === false) throw new Error(data?.message || "Subtype info failed");

      setSubTypeCount(String(data?.data?.count ?? ""));
      setSubTypeTooltip(String(data?.data?.tooltip ?? ""));
    } catch (e) {
      console.error(e);
      setSubTypeCount("");
      setSubTypeTooltip("");
    }
  };

  const handleSaveGroup = async () => {
    if (!vehicleTypeId) {
      toast.error("VehicleTypeID missing");
      return;
    }

    if (selectedSubTypeToGroup === "Select") {
      toast.warning("Please select a subtype to group");
      return;
    }

    setLoading(true);
    try {
      const { data } = await axios.post(
        `${API}/save-group`,
        {
          vehicleTypeId,
          selectedSubTypeId: Number(selectedSubTypeToGroup),
        },
        {
          headers: commonHeaders,
        }
      );

      if (data?.success === false) throw new Error(data?.message || "Save failed");

      toast.success(data?.message || "Saved successfully");
      setSelectedSubTypeToGroup("Select");
      setSubTypeCount("");
      setSubTypeTooltip("");

      await fetchList();
      await fetchInit();
    } catch (e) {
      console.error(e);
      toast.error(e?.response?.data?.message || e?.message || "Save failed");
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveSelected = async () => {
    if (!selectedIds.length) {
      toast.warning("Select at least one row to remove");
      return;
    }

    setLoading(true);
    try {
      const { data } = await axios.post(
        `${API}/remove`,
        { removeIds: selectedIds },
        { headers: commonHeaders }
      );

      if (data?.success === false) throw new Error(data?.message || "Remove failed");

      toast.success(data?.message || "Removed successfully");
      await fetchList();
    } catch (e) {
      console.error(e);
      toast.error(e?.response?.data?.message || e?.message || "Remove failed");
    } finally {
      setLoading(false);
    }
  };

  // --------------- Effects ----------------

  useEffect(() => {
    if (!vehicleTypeId) {
      toast.error("VehicleTypeID missing. Use ?vehicleTypeId=1 or set sessionStorage VehicleTypeID");
      return;
    }

    fetchInit();
    fetchList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vehicleTypeId, commonHeaders]);

  // --------------- Selection ----------------

  const toggleSelectAll = () => {
    if (allChecked) setSelectedIds([]);
    else setSelectedIds(rows.map(getRowId).filter(Boolean));
  };

  const toggleOne = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  // --------------- UI ----------------

  return (
    <VehicleLayoutWrapper>
      <HeaderBar
        title={<span className="font-dmserif text-3xl">VEHICLE TYPE WITH SUB TYPE</span>}
        setSkin={setSkin}
        currentSkin={skin}
      />

      <div
        className={`p-6 mt-6 rounded-2xl shadow-xl border ${
          isDark
            ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
            : `bg-white ${currentSkin.glass} text-black`
        }`}
      >
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <div className="flex items-center gap-3">
            <h1 className={`text-2xl font-bold ${isDark ? "text-white" : "text-gray-900"}`}>
              Vehicle Type With Sub Type
            </h1>
            <span
              className={`px-3 py-1 rounded-full text-sm font-medium ${
                isDark ? "bg-blue-900 text-blue-100" : "bg-blue-100 text-blue-800"
              }`}
            >
              {rows.length} Records
            </span>
          </div>

          <div className="flex gap-3">
            <button
              onClick={() => setShowFilters((s) => !s)}
              className={`flex items-center gap-2 px-4 py-2 border rounded-lg transition-colors ${
                isDark
                  ? "border-gray-600 text-white hover:bg-gray-800" + (showFilters ? " bg-gray-800" : "")
                  : "border-gray-300 text-gray-700 hover:bg-gray-50" + (showFilters ? " bg-gray-50" : "")
              }`}
            >
              <FaFilter className="w-4 h-4" />
              {showFilters ? "Hide Filters" : "Show Filters"}
            </button>

            <button
              onClick={() => navigate(-1)}
              className={`flex items-center gap-2 px-4 py-2 border rounded-lg transition-colors ${
                isDark
                  ? "border-gray-600 text-white hover:bg-gray-800"
                  : "border-gray-300 text-gray-700 hover:bg-gray-50"
              }`}
            >
              <FaArrowLeft className="w-4 h-4" />
              Back
            </button>
          </div>
        </div>

        {/* Filters */}
        {showFilters && (
          <div
            className={`rounded-xl p-6 mb-6 border ${
              isDark ? "bg-gray-800 border-gray-700" : "bg-gray-50 border-gray-200"
            }`}
          >
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* Main Type */}
              <div>
                <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                  Vehicle Main Type
                </label>

                <select
                  value={selectedMainType}
                  disabled
                  className={`w-full px-3 py-2 border rounded-lg ${
                    isDark ? "bg-gray-700 border-gray-600 text-white" : "bg-white border-gray-300 text-gray-900"
                  }`}
                >
                  {(mainTypeDropdown || []).map((o, i) => {
                    const id = pick(o, ["fld_vehicletypeid", "Value", "id"], "");
                    const name = pick(o, ["fld_vehicletypename", "Text", "name"], "");
                    return (
                      <option key={i} value={id}>
                        {name}
                      </option>
                    );
                  })}
                </select>
              </div>

              {/* Grouping dropdown */}
              <div>
                <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                  Select Sub Type To Group
                </label>

                <select
                  value={selectedSubTypeToGroup}
                  onChange={(e) => {
                    setSelectedSubTypeToGroup(e.target.value);
                    fetchSubTypeInfo(e.target.value);
                  }}
                  className={`w-full px-3 py-2 border rounded-lg ${
                    isDark ? "bg-gray-700 border-gray-600 text-white" : "bg-white border-gray-300 text-gray-900"
                  }`}
                >
                  <option value="Select">Select</option>
                  {(groupingDropdown || []).map((o, i) => {
                    const id = pick(o, ["fld_vehicletypeid", "Value", "id"], "");
                    const name = pick(o, ["fld_vehicletypename", "Text", "name"], "");
                    return (
                      <option key={i} value={id}>
                        {name}
                      </option>
                    );
                  })}
                </select>

                {selectedSubTypeToGroup !== "Select" && (
                  <div className="mt-2 text-xs">
                    <div className={`${isDark ? "text-gray-200" : "text-gray-700"}`}>
                      Vehicles Count: <b>{subTypeCount || "0"}</b>
                    </div>

                    {subTypeTooltip ? (
                      <div
                        className={`mt-1 p-2 rounded border whitespace-pre-line ${
                          isDark ? "border-gray-600 bg-gray-900/40" : "border-gray-200 bg-white"
                        }`}
                        title={subTypeTooltip}
                      >
                        {subTypeTooltip}
                      </div>
                    ) : null}
                  </div>
                )}
              </div>

              {/* Index */}
              <div>
                <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                  Vehicle Type Index
                </label>

                <input
                  value={vehicleTypeIndex}
                  disabled
                  className={`w-full px-3 py-2 border rounded-lg ${
                    isDark ? "bg-gray-700 border-gray-600 text-white" : "bg-white border-gray-300 text-gray-900"
                  }`}
                />
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={handleSaveGroup}
                disabled={loading || selectedSubTypeToGroup === "Select"}
                className={`flex items-center gap-2 px-6 py-3 text-white rounded-lg font-semibold shadow-lg ${
                  loading || selectedSubTypeToGroup === "Select"
                    ? "bg-gray-400 cursor-not-allowed"
                    : `${buttonClass} ${hoverClass}`
                }`}
              >
                <FaSave />
                {loading ? "Saving..." : "Save"}
              </button>
            </div>
          </div>
        )}

        {/* Bulk remove */}
        {selectedIds.length > 0 && (
          <div
            className={`rounded-lg p-4 mb-6 ${
              isDark ? "bg-blue-900/30 border border-blue-700" : "bg-blue-50 border border-blue-200"
            }`}
          >
            <div className="flex items-center justify-between">
              <div className={`font-semibold ${isDark ? "text-blue-200" : "text-blue-800"}`}>
                {selectedIds.length} item(s) selected
              </div>

              <button
                onClick={handleRemoveSelected}
                disabled={loading}
                className={`flex items-center gap-2 px-4 py-2 text-white rounded-lg font-medium ${
                  loading ? "bg-gray-400 cursor-not-allowed" : "bg-red-600 hover:bg-red-700"
                }`}
              >
                <FaTrash />
                Remove Selected
              </button>
            </div>
          </div>
        )}

        {/* Table */}
        <div className={`rounded-xl shadow-sm border overflow-hidden ${isDark ? "border-gray-700" : "border-gray-200"}`}>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className={`${buttonClass} text-white`}>
                <tr>
                  <th className="px-4 py-4 text-left font-semibold w-16">S.No</th>
                  <th className="px-4 py-4 text-left font-semibold">Vehicle Sub Type</th>
                  <th className="px-4 py-4 text-center font-semibold w-40">
                    <input
                      type="checkbox"
                      checked={allChecked}
                      onChange={toggleSelectAll}
                      className="mr-2"
                    />
                    Select All
                  </th>
                </tr>
              </thead>

              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {loading ? (
                  <tr>
                    <td colSpan={3} className={`px-4 py-10 text-center ${isDark ? "text-gray-200" : "text-gray-700"}`}>
                      Loading...
                    </td>
                  </tr>
                ) : rows.length === 0 ? (
                  <tr>
                    <td colSpan={3} className={`px-4 py-10 text-center ${isDark ? "text-gray-200" : "text-gray-700"}`}>
                      No records
                    </td>
                  </tr>
                ) : (
                  rows.map((r, idx) => {
                    const id = getRowId(r);
                    const name = getRowName(r);
                    const tooltip = r?._tooltip || "";

                    return (
                      <tr
                        key={id || idx}
                        title={tooltip}
                        className={`hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors duration-200 ${
                          isDark ? "bg-gray-900" : "bg-white"
                        }`}
                      >
                        <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-white">
                          {idx + 1}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">{name}</td>
                        <td className="px-4 py-3 text-center">
                          <input
                            type="checkbox"
                            checked={selectedIds.includes(id)}
                            onChange={() => toggleOne(id)}
                            className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                          />
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>

        <ToastContainer
          position="top-right"
          autoClose={3000}
          closeOnClick
          pauseOnHover
          theme={isDark ? "dark" : "light"}
        />
      </div>
    </VehicleLayoutWrapper>
  );
}